"use client";

import Cookies from "js-cookie";
import ItemCard from "./ItemCard";
import dynamic from "next/dynamic";
import useEngineOil from "./useEngineOil";
import { useAppSelector } from "@/lib/store";
import EngineOilBanner from "./EngineOilBanner";
import { getPageType } from "@/lib/utils/functions";
import { memo, useEffect, useMemo, useRef } from "react";
import { useAddToTrolley } from "@/lib/hooks/useAddToTrolley";
import { updatePlpTitle } from "@/lib/store/reducers/uiState";
import CustomPagination from "@/components/common/CustomPagination";
import { no_products_found, viewItemListAnalytics } from "@/lib/gtm";
import {
  pageSummaryInfo,
  createItemListPayloadForEngineOilPage,
} from "@/lib/utils/helpers/plp";

const NoListingItem = dynamic(() => import("@/components/misc/NoListingItem"));
const SelectionBox = dynamic(() => import("@/components/common/SelectionBox"), {
  ssr: false,
});
const BranchModal = dynamic(() => import("@/components/product/BranchModal"), {
  ssr: false,
});
const NoVehicle = dynamic(() =>
  import("../ProductListing/components/NoVehicle"),
);
const FitmentInfoModal = dynamic(
  () => import("../ProductListing/components/FitmentInfoModal"),
  {
    ssr: false,
  },
);
const ShowSpecificFilter = dynamic(
  () => import("../ProductListing/components/ShowSpecificFilter"),
  {
    ssr: false,
  },
);

const MemoEngineOilBanner = memo(EngineOilBanner);

const EngineOilListing = ({
  pageData,
  category,
  userVehicle,
  bannerImages,
  constantKeys,
  serviceRes,
}) => {
  const lastSentProductIds = useRef("");
  const { appInitailized } = useAppSelector((state) => state.uiState);
  const { handleAddToTrolley } = useAddToTrolley();
  const {
    data: {
      items,
      vehicle,
      skuList,
      filters,
      filterOpt,
      sortOrder,
      pageLimit,
      branchList,
      isFetching,
      modalStatus,
      showFitment,
      currentPage,
      fitmentInfo,
      showSpecific,
      searchParams,
      selectedItem,
      additionalCat,
      totalProducts,
    },
    methods: {
      t,
      dispatch,
      handlePageChange,
      handleCheckStock,
      toggleBranchModal,
      toggleFitmentModal,
      handleBranchChange,
      handlePageSelection,
      handleSizeSelection,
      handleToggleCategory,
      handleBranchSelection,
      handleVehicleSpecific,
      handleFilterSelection,
      handleSortingSelection,
    },
  } = useEngineOil({
    ...pageData,
    ...constantKeys,
    userVehicle,
    catId: category.id,
    componentId: category?.component_id,
    serviceRes,
  });

  const isNoResult = useMemo(() => {
    return !isFetching && !totalProducts;
  }, [isFetching, totalProducts]);

  const categoryInfo = useMemo(() => {
    const catTree = category?.breadcrumb_paths || [];
    const parentId = catTree[catTree.length - 2]?.category_id || null;
    return {
      parentId,
      currentId: category?.id,
    };
  }, [category?.id]);

  const pageSummary = useMemo(() => {
    return {
      has_products: totalProducts > 0,
      show_fitment_alert: totalProducts > 0,
      text: t(
        "filters.result_summary",
        pageSummaryInfo(currentPage, pageLimit, totalProducts),
      ),
    };
  }, [currentPage, pageLimit, totalProducts]);

  useEffect(() => {
    if (appInitailized && !isFetching && totalProducts === 0) {
      dispatch(updatePlpTitle(""));
      const vehicleSelectedOrNot = Cookies.get(
        constantKeys.cookieKeys.selectedVehicle,
      );
      no_products_found({
        page_type: getPageType(),
        ...(showSpecific &&
          vehicleSelectedOrNot && {
            manufacturer: vehicle?.vehicle?.make,
            model: vehicle?.vehicle?.model,
            ...(vehicle?.vehicle?.engineSize && {
              engine: vehicle?.vehicle?.engineSize,
            }),
            years:
              vehicle?.vehicle?.modelVersion?.match(/\d{4}/)[0] ||
              vehicle?.vehicle?.dateFirstRegistered?.match(/^(\d{4})/)[0],
          }),
      });
    }
  }, [appInitailized, totalProducts, showSpecific?.current]);

  useEffect(() => {
    if (
      appInitailized &&
      pageSummary?.has_products &&
      items &&
      items.length > 0
    ) {
      // Create a unique signature for the current product list
      const currentProductIds = items.map((p) => p.art_nr).join(",");

      if (lastSentProductIds.current !== currentProductIds) {
        viewItemListAnalytics(
          createItemListPayloadForEngineOilPage(items, "Product Listing"),
        );
        lastSentProductIds.current = currentProductIds;
      }
    }
  }, [items, appInitailized, pageSummary?.has_products]);

  if (searchParams.has("make") && searchParams.has("model")) {
    return (
      <NoVehicle>
        {bannerImages && <MemoEngineOilBanner bannerImages={bannerImages} />}
      </NoVehicle>
    );
  }

  return (
    <div className="w-12/12 flex flex-col">
      {bannerImages && pageSummary.has_products && (
        <MemoEngineOilBanner bannerImages={bannerImages} />
      )}

      {pageSummary.has_products && (
        <SelectionBox
          is3BO={true}
          filters={filters}
          isCarParts={false}
          pageSize={pageLimit}
          sortOrder={sortOrder}
          isEngineOil={true}
          categoryId={category.id}
          filterOptions={filterOpt}
          totalItems={totalProducts}
          selectedCat={additionalCat}
          additional={pageData.additional}
          sortOptions={pageData.sort_options}
          handlePageSelection={handlePageSelection}
          pageSizeOptions={pageData.pagesize_options}
          handleToggleCategory={handleToggleCategory}
          handleFilterSelection={handleFilterSelection}
          handleSortingSelection={handleSortingSelection}
        />
      )}

      <div className="container h-12/12" id="product-list">
        <ShowSpecificFilter
          t={t}
          is3BO={true}
          category={category}
          isFetching={isFetching}
          showSpecific={showSpecific}
          showAlert={pageSummary.show_fitment_alert}
          handleVehicleSpecific={handleVehicleSpecific}
        />

        {pageSummary.has_products && (
          <>
            <div className="row xs:mb-24 mt-10 mb-12">
              <div className="col w-12/12 xs:min-h-[20px] min-h-[28px]">
                <p className="text-primary-black-600 xs:text-body-text-3 text-body-text-3-m my-auto block w-12/12 xs:mb-0 mb-2">
                  {pageSummary.text}
                </p>
              </div>
            </div>

            <div className="row">
              <div className="col w-12/12">
                {items.map((item, i) => (
                  <div className="plp-box" key={`${item.art_nr}${i}`}>
                    <ItemCard
                      t={t}
                      ga4Index={i}
                      product={item}
                      skuList={skuList}
                      vehicle={vehicle}
                      showSpecific={showSpecific}
                      assets={pageData.config_icons}
                      cookiesKeys={constantKeys.cookies}
                      showBranchModal={toggleBranchModal}
                      handleAddToTrolley={handleAddToTrolley}
                      toggleFitmentModal={toggleFitmentModal}
                      handleSizeSelection={handleSizeSelection}
                    />
                  </div>
                ))}
              </div>
            </div>
            {!isFetching && (
              <div className="row">
                <div className="col plp-pagination-box w-12/12">
                  <p className="text-primary-black-600 xs:text-body-text-3 text-body-text-3-m my-auto block w-12/12 xs:mb-0 mb-2">
                    {pageSummary.text}
                  </p>
                  <CustomPagination
                    scrollToTop
                    page={currentPage}
                    total={totalProducts}
                    itemsPerPage={pageLimit}
                    setPage={handlePageChange}
                    currentURL={pageData.current_url}
                    searchParams={pageData.page_query}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {isNoResult && (
          <NoListingItem
            className={isFetching ? "hidden" : ""}
            parentCategoryId={categoryInfo.parentId}
            currentCategoryId={categoryInfo.currentId}
            heading={t("messages.no_results.specific_product")}
            message={t("messages.no_results.specific_product_text")}
          />
        )}

        {showFitment && (
          <FitmentInfoModal
            isOpen={showFitment}
            infoArr={fitmentInfo}
            handleClose={toggleFitmentModal}
          />
        )}

        <BranchModal
          t={t}
          isEngineOil
          vehicle={vehicle}
          item={selectedItem}
          branchList={branchList}
          branchModalOpen={modalStatus}
          handleCheckStock={handleCheckStock}
          toggleBranchModal={toggleBranchModal}
          onBranchRadioSelection={handleBranchChange}
          handleBranchSelection={handleBranchSelection}
        />
      </div>
    </div>
  );
};

export default EngineOilListing;
